import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import {
  FormControlSizes,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Toggle,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { isIncludeCashPositionsTab, type PositionsTabProps } from '../../../../Blotters/PositionsV3/types';
import { MobileCompactCardsToggle } from '../MobileCompactCardsToggle';
import { MobileShowAllDecimalsToggle } from '../MobileShowAllDecimalsToggle';

interface MobilePositionsSettingsTogglesProps<TTab extends PositionsTabProps> {
  tab: TTab;
  onUpdateTab: (tab: TTab) => void;
}

export function MobilePositionsSettingsToggles<TTab extends PositionsTabProps>({
  tab,
  onUpdateTab,
}: MobilePositionsSettingsTogglesProps<TTab>) {
  const mixpanel = useMixpanel();
  const showZeroBalances = tab.showZeroBalances ?? false;
  const includeCash = isIncludeCashPositionsTab(tab) ? (tab.includeCash ?? true) : false;

  const handleShowZeroBalancesChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
      onUpdateTab({ ...tab, showZeroBalances: value });
    },
    [mixpanel, onUpdateTab, tab]
  );

  const handleIncludeCashChange = useCallback(
    (value: boolean) => {
      if (!isIncludeCashPositionsTab(tab)) {
        return;
      }

      mixpanel.track(MixpanelEvent.ToggleIncludeCash, {
        [MixpanelEventProperty.Enabled]: value,
        [MixpanelEventProperty.Source]:
          tab.tabType === 'Unified'
            ? MixpanelEventSource.UnifiedPositionsBlotter
            : MixpanelEventSource.SubAccountPositionsBlotter,
      });
      onUpdateTab({ ...tab, includeCash: value } as TTab);
    },
    [mixpanel, onUpdateTab, tab]
  );

  return (
    <>
      <MobileCompactCardsToggle type="Position" />
      <Toggle
        label="Show Zero Balances"
        size={FormControlSizes.Default}
        checked={showZeroBalances}
        onChange={handleShowZeroBalancesChange}
        fullWidth
      />
      {isIncludeCashPositionsTab(tab) && (
        <Toggle
          label="Include Cash"
          size={FormControlSizes.Default}
          tooltip={CONTROL_TOOLTIPS.includeCash}
          checked={includeCash}
          onChange={handleIncludeCashChange}
          fullWidth
        />
      )}
      <MobileShowAllDecimalsToggle />
    </>
  );
}
